import { fieldClass } from "@/components/triage/date-choice";
import type { PhaseRecord } from "@/lib/api";
import { type Range, rangeLabel, sameRange } from "@/lib/gantt";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { useEffect, useState } from "react";

const button = css({
  fontFamily: "sans",
  fontSize: "[13px]",
  px: "12",
  py: "6",
  borderRadius: "4",
  border: "default",
  bg: "transparent",
  color: "text",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "surface.hovered" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
  _disabled: { color: "text.disabled", cursor: "not-allowed" },
});
const primary = css({
  fontFamily: "sans",
  fontSize: "[13px]",
  px: "12",
  py: "6",
  borderRadius: "4",
  border: "none",
  bg: "bg.brand.bold",
  color: "text.inverse",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "bg.brand.bold.hovered" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
  _disabled: { bg: "bg.disabled", color: "text.disabled", cursor: "not-allowed" },
});
const label = css({
  display: "flex",
  flexDir: "column",
  gap: "4",
  fontFamily: "mono",
  fontSize: "12",
  color: "text.subtle",
});

interface DatePanelProps {
  phase: PhaseRecord;
  /** The range the bar shows now: staged, else the plan. */
  current: Range;
  plan: Range;
  block: string | null;
  onDraft: (r: Range | null) => void;
  onStage: (r: Range) => void;
  onClose: () => void;
}

/** The keyboard and touch path: start and end date fields for the selected bar. */
export function DatePanel({
  phase,
  current,
  plan,
  block,
  onDraft,
  onStage,
  onClose,
}: DatePanelProps) {
  const [start, setStart] = useState(current.start);
  const [end, setEnd] = useState(current.end);
  useEffect(() => {
    setStart(current.start);
    setEnd(current.end);
  }, [current.start, current.end]);

  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(start) && /^\d{4}-\d{2}-\d{2}$/.test(end) && start <= end;
  const changed = valid && !sameRange({ start, end }, current);

  const update = (s: string, e: string) => {
    setStart(s);
    setEnd(e);
    const ok = /^\d{4}-\d{2}-\d{2}$/.test(s) && /^\d{4}-\d{2}-\d{2}$/.test(e) && s <= e;
    onDraft(ok && !sameRange({ start: s, end: e }, current) ? { start: s, end: e } : null);
  };

  return (
    <Box
      as="section"
      aria-label={`Dates for ${phase.name}`}
      display="flex"
      flexDir="column"
      gap="12"
      p="16"
      border="default"
      borderRadius="4"
      bg="surface"
    >
      <Text fontSize="14" fontWeight="medium" color="text.bold">
        {phase.name}
      </Text>
      <Text textStyle="mono.xs" color="text.subtlest">
        Plan in the vault: {rangeLabel(plan)}
      </Text>
      {block ? (
        <Text fontSize="14" color="text.warning">
          {block}
        </Text>
      ) : (
        <>
          <Box display="flex" gap="12" flexWrap="wrap">
            <label className={label}>
              Start
              <input
                type="date"
                className={fieldClass}
                value={start}
                max={end || undefined}
                onChange={(e) => update(e.target.value, end)}
              />
            </label>
            <label className={label}>
              End
              <input
                type="date"
                className={fieldClass}
                value={end}
                min={start || undefined}
                onChange={(e) => update(start, e.target.value)}
              />
            </label>
          </Box>
          {!valid && (
            <Text textStyle="mono.xs" color="text.danger">
              The start must be on or before the end.
            </Text>
          )}
        </>
      )}
      <Box display="flex" gap="8" flexWrap="wrap">
        {!block && (
          <button
            type="button"
            className={primary}
            disabled={!changed}
            onClick={() => onStage({ start, end })}
          >
            Stage
          </button>
        )}
        {!block && !sameRange(current, plan) && (
          <button type="button" className={button} onClick={() => onStage(plan)}>
            Back to plan
          </button>
        )}
        <button
          type="button"
          className={button}
          onClick={() => {
            onDraft(null);
            onClose();
          }}
        >
          Close
        </button>
      </Box>
    </Box>
  );
}
